import React from "react";

import { faEdit } from "@fortawesome/pro-regular-svg-icons";
import { Controller, useFormContext } from "react-hook-form";

import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import type { Option } from "src/base-components/Select";
import { Select } from "src/base-components/Select";
import { ErrorHint } from "src/design-system/ErrorHint";
import { IconButton } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import {
  type AICredentialFormInputs,
  type CredentialEnvName,
  type OpenAIRegion,
  REGION_TO_BASE_URL,
} from "src/settings/credentials/model";

const OPENAI_REGION_OPTIONS: Option[] = [
  { key: "global", value: "Global" },
  { key: "us", value: "United States" },
  { key: "eu", value: "Europe" },
];

type Props = {
  env: CredentialEnvName;
  isEditing: boolean;
  secretEditable: boolean;
  onEnableSecret: () => void;
  onRegionChange?: () => void;
};

export const OpenAIFields: React.FC<Props> = ({
  env,
  isEditing,
  secretEditable,
  onEnableSecret,
  onRegionChange,
}) => {
  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = useFormContext<AICredentialFormInputs>();

  const apiKeyField = `envConfigs.${env}.apiKey` as const;
  const regionField = `envConfigs.${env}.region` as const;
  const baseUrlField = `envConfigs.${env}.baseUrl` as const;

  const apiKeyError = errors.envConfigs?.[env]?.apiKey;

  const showMasked = isEditing && !secretEditable;

  return (
    <>
      <FormItem gap="sm" label="API Key" isRequired>
        <div className="flex w-full items-center gap-x-2">
          <div className="flex-1">
            <Input
              {...register(apiKeyField, {
                required: secretEditable ? "API key is required" : false,
                validate: {
                  format: (v) =>
                    !v ||
                    v.startsWith("sk-") ||
                    "API key must start with 'sk-'",
                  // Mirrors the backend rejection so the error is inline
                  // instead of a post-submit 422 toast.
                  notAnthropic: (v) =>
                    !v ||
                    !v.startsWith("sk-ant-") ||
                    "API key has Anthropic prefix 'sk-ant-'; expected an OpenAI key",
                },
              })}
              disabled={showMasked}
              placeholder={showMasked ? "••••••••" : "sk-..."}
              type="password"
              fullWidth
            />
          </div>
          {showMasked && (
            <Tooltip placement="right" title="Edit API key" asChild>
              <IconButton icon={faEdit} size="xs" onClick={onEnableSecret} />
            </Tooltip>
          )}
        </div>
        {apiKeyError?.message && <ErrorHint>{apiKeyError.message}</ErrorHint>}
      </FormItem>
      <FormItem gap="sm" label="Region">
        <Controller
          control={control}
          name={regionField}
          render={({ field }) => (
            <Select
              options={OPENAI_REGION_OPTIONS}
              value={field.value ?? "global"}
              onChange={(value) => {
                field.onChange(value);
                // Only an explicit region change overwrites the carried-over
                // stored base_url with the canonical region URL.
                setValue(
                  baseUrlField,
                  REGION_TO_BASE_URL[value as OpenAIRegion],
                  { shouldDirty: true },
                );
                onRegionChange?.();
              }}
            />
          )}
        />
      </FormItem>
    </>
  );
};
